export type OpenRouterCatalogMetadata = {
  fetchedAt: string;
  model: {
    id: string;
    name: string;
    description: string | null;
    contextLength: number | null;
    pricing: {
      promptPer1M: number | null;
      completionPer1M: number | null;
    };
    supportedParameters: string[];
    topProvider: {
      contextLength: number | null;
      maxCompletionTokens: number | null;
      isModerated: boolean;
    } | null;
  };
  endpoints: Array<{
    name: string;
    providerName: string;
    tag: string;
    contextLength: number | null;
    pricing: {
      promptPer1M: number | null;
      completionPer1M: number | null;
    };
    isZdr: boolean;
    dataPolicy: {
      training: boolean | null;
      retainsPrompts: boolean | null;
    };
  }>;
  privacy: {
    hasZdrEndpoint: boolean;
    zdrEndpointCount: number;
    endpointCount: number;
    mayTrainOnPrompts: boolean;
    retainsData: boolean;
    securityTier: 'secure' | 'caution' | 'unsafe';
    warnings: string[];
  };
};

export type AdminLlmModel = {
  id: string;
  openrouterModelId: string;
  displayName: string;
  enabled: boolean;
  providerTag: string | null;
  openrouterMetadata: OpenRouterCatalogMetadata | null;
  createdAt: string;
  updatedAt: string;
};

export type LlmProfileId = 'fast' | 'balanced' | 'capable';

export type AdminLlmProfile = {
  profile: LlmProfileId;
  label: string;
  summary: string;
  model: AdminLlmModel | null;
};

export type LlmCatalogSource = {
  kind: 'benchmark' | 'usage';
  task?: 'intelligence' | 'coding' | 'agentic';
  rank: number;
};

export type LlmCatalogCandidate = {
  modelId: string;
  name: string;
  contextLength: number | null;
  promptPer1M: number | null;
  completionPer1M: number | null;
  supportsTools: boolean;
  intelligenceIndex: number | null;
  codingIndex: number | null;
  agenticIndex: number | null;
  throughputP50: number | null;
  latencyP50Sec: number | null;
  usageTokens: number | null;
  sources: LlmCatalogSource[];
  hasZdr: boolean;
  privacy: OpenRouterCatalogMetadata['privacy'] | null;
  allowlistId: string | null;
  enabled: boolean;
  displayName: string | null;
};

export type LlmSuggestionFactor = {
  key: string;
  label: string;
  weight: number;
  normalized: number;
};

export type LlmProfileSuggestion = {
  modelId: string;
  score: number;
  factors: LlmSuggestionFactor[];
  allowlistId: string | null;
  enabled: boolean;
  displayName: string | null;
};

export type LlmCatalogResponse = {
  fetchedAt: string | null;
  candidates: LlmCatalogCandidate[];
  suggestions: Record<LlmProfileId, LlmProfileSuggestion[]>;
};

export type LlmUsageBucket = {
  calls: number;
  promptTokens: number;
  completionTokens: number;
  cost: number;
};

export type LlmUsageSummary = {
  totals: LlmUsageBucket;
  byDay: Array<LlmUsageBucket & { day: string }>;
  byFeature: Array<LlmUsageBucket & { feature: string }>;
  byModel: Array<LlmUsageBucket & { model: string }>;
  byMember: Array<LlmUsageBucket & { memberId: string | null; memberName: string | null }>;
};

export type LlmCallRow = {
  id: string;
  createdAt: string;
  feature: string;
  operation: string;
  profile: string | null;
  model: string;
  memberId: string | null;
  memberName: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  cost: number | null;
  latencyMs: number | null;
  status: string;
  error: string | null;
  conversationId: string | null;
  financeBatchId: string | null;
  messageId: string | null;
  openrouterGenerationId: string | null;
};

export function formatPricePer1M(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  if (value < 0.01) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(2)}`;
}

export function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value === 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

export function featureLabel(feature: string): string {
  switch (feature) {
    case 'chat':
      return 'Chat';
    case 'finance_import':
      return 'Finance import';
    case 'embeddings':
      return 'Embeddings';
    case 'search':
      return 'Search';
    default:
      return feature;
  }
}

export function formatDayLabel(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function retentionSummary(
  privacy: OpenRouterCatalogMetadata['privacy'] | null | undefined,
): string {
  if (!privacy) return 'Retention details are not loaded yet.';
  if (!privacy.hasZdrEndpoint) {
    return 'No zero-data-retention route. This model cannot be used.';
  }
  const zdr = `${privacy.zdrEndpointCount} of ${privacy.endpointCount} routes are zero-retention.`;
  const train = privacy.mayTrainOnPrompts
    ? 'Other routes may train on prompts.'
    : 'No listed route trains on prompts.';
  const retain = privacy.retainsData
    ? 'Other routes may retain prompts.'
    : 'No listed route retains prompts.';
  return `${zdr} ${train} ${retain}`;
}

export function formatContextTokens(value: number | null | undefined): string {
  if (value == null) {
    return '—';
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }
  return String(value);
}
